import React, { useState, useEffect } from 'react';
import html2pdf from 'html2pdf.js';

// Define types for our data structures
interface Question {
  id: string;
  text: string;
  options: string[];
  explanation: string;
}

interface CategoryScore {
  raw: number;
  max: number;
  percentage: number;
}

interface Scores {
  categories: Record<string, CategoryScore>;
  overall: {
    raw: number;
    max: number;
    percentage: number;
  };
}

interface Recommendation {
  category: string;
  maturity: string;
  recommendations: string[];
}

type AnswersType = Record<string, number>;

const InsiderThreatRiskProfiler = () => {
  const [currentStep, setCurrentStep] = useState<number>(-1);
  const [answers, setAnswers] = useState<AnswersType>({});
  const [showResults, setShowResults] = useState<boolean>(false);
  const [industryType, setIndustryType] = useState<string>("general");
  const [companySize, setCompanySize] = useState<string>("medium");
  const [pdfReady, setPdfReady] = useState<boolean>(false);
  
  // Load the html2pdf library when component mounts
  // Central gate (CISO Marketplace / ThreatWatch): gated UP FRONT — first answer triggers requireRun
  // (member-free / credits / Tools plan / one-time guest pay). gate.js shows the unlock overlay.
  const [started, setStarted] = useState<boolean>(false);
  const ensureStarted = async (): Promise<boolean> => {
    if (started) return true;
    const gate = (window as unknown as { csmGate?: { requireRun?: () => Promise<boolean> } }).csmGate;
    const ok = gate?.requireRun ? await gate.requireRun() : true; // graceful: no gate → allow
    if (ok) setStarted(true);
    return ok;
  };

  useEffect(() => {
    // Set pdfReady to true once component is mounted and html2pdf should be available
    setPdfReady(true);
    try {
      const saved = localStorage.getItem('insiderrisk_answers');
      if (saved) setAnswers(JSON.parse(saved));
    } catch { /* ignore */ }
    // Resume after a guest-pay / sign-in bounce
    const gate = (window as unknown as { csmGate?: { requireRun?: () => Promise<boolean> } }).csmGate;
    if (new URLSearchParams(window.location.search).get('gr_session') && gate?.requireRun) {
      gate.requireRun().then((ok) => { if (ok) setStarted(true); });
    }
  }, []);

  const categories = [
    "Remote Work Controls",
    "Identity Verification",
    "Data Access Monitoring",
    "Employee Behavior Analytics",
    "Offboarding Procedures",
    "AI & GenAI Controls"
  ];
  
  const questions: Question[][] = [
    // Remote Work Controls
    [
      { 
        id: "remote_policy", 
        text: "How comprehensive is your remote work security policy in addressing modern threats?", 
        options: [
          "No formal policy exists for remote work security", 
          "Basic policy covering passwords and VPN usage", 
          "Comprehensive policy with specific controls for different risk levels", 
          "Advanced policy with regular updates, simulations, and threat-specific controls"
        ],
        explanation: "A comprehensive remote work policy should address device management, access controls, data handling, secure communications, and incident response procedures specific to remote environments."
      },
      { 
        id: "remote_auth", 
        text: "What authentication methods protect your remote work infrastructure from credential theft and identity fraud?", 
        options: [
          "Password-only authentication without complexity requirements", 
          "Password plus email-based MFA (can be intercepted)", 
          "Password plus app-based MFA with push notifications", 
          "Passwordless authentication with biometrics and hardware security keys"
        ],
        explanation: "Advanced authentication methods protect against credential theft, phishing, and session hijacking that target remote workers."
      },
      { 
        id: "remote_device", 
        text: "How do you secure and manage devices used by remote workers against impersonation and compromise?", 
        options: [
          "No formal management of remote devices", 
          "Basic inventory tracking and required antivirus", 
          "MDM solution deployed with policy enforcement", 
          "Zero-trust architecture with continuous device validation and conditional access"
        ],
        explanation: "Device management ensures that only authorized, secure, and compliant devices can access corporate resources."
      },
      { 
        id: "remote_monitor", 
        text: "What capabilities do you have to detect suspicious activities by remote workers, including potential impersonation?", 
        options: [
          "No monitoring of remote work activities", 
          "Basic time tracking and VPN connection logs", 
          "Endpoint monitoring with security agents and DLP", 
          "Comprehensive behavior analytics with AI-based anomaly detection"
        ],
        explanation: "Advanced monitoring can detect unusual patterns that may indicate compromised credentials, impersonation, or malicious activity."
      },
      { 
        id: "remote_training", 
        text: "How do you train remote employees on security risks specific to remote environments?", 
        options: [
          "No specialized security training for remote workers", 
          "Basic annual security awareness covering remote work", 
          "Quarterly training with remote-specific scenarios and phishing tests", 
          "Continuous adaptive training based on emerging threats and employee behavior"
        ],
        explanation: "Specialized security training helps remote employees identify and respond to threats unique to remote environments."
      }
    ],
    // Identity Verification
    [
      { 
        id: "id_onboarding", 
        text: "What identity verification procedures do you use during employee onboarding to prevent impersonation?", 
        options: [
          "Basic document checking without verification", 
          "Document verification plus basic background check", 
          "Multiple document verification, comprehensive background and credit checks", 
          "Advanced verification including liveness detection in video interviews, document validation, and continuous identity monitoring"
        ],
        explanation: "Comprehensive identity verification during onboarding is the first defense against imposters and identity fraud."
      },
      { 
        id: "id_ongoing", 
        text: "How frequently do you re-verify employee identities and credentials throughout employment?", 
        options: [
          "Identity is only verified during initial hiring", 
          "Annual recertification of credentials and access", 
          "Quarterly identity verification checks and access reviews", 
          "Continuous identity verification through behavioral biometrics and contextual authentication"
        ],
        explanation: "Periodic re-verification ensures that access rights remain appropriate and can detect compromised credentials."
      },
      { 
        id: "id_deepfake", 
        text: "What measures do you have to detect deepfakes or digital manipulation during video interviews or virtual meetings?", 
        options: [
          "No specific measures to detect deepfakes", 
          "Basic awareness training for recruiters on deepfake detection", 
          "Specialized deepfake detection tools during hiring processes", 
          "Advanced AI-based detection systems with multiple verification protocols and liveness detection"
        ],
        explanation: "As deepfake technology becomes more sophisticated, organizations need specific controls to detect artificial video and audio."
      },
      { 
        id: "id_access", 
        text: "How do you manage digital identity and access credentials to prevent unauthorized access?", 
        options: [
          "Manual provisioning and management of credentials", 
          "Basic IAM system for centralized credential management", 
          "Advanced IAM with workflow automation and privilege management", 
          "Zero-trust identity framework with continuous validation and just-in-time access"
        ],
        explanation: "Sophisticated identity management reduces the risk of credential abuse and unauthorized access."
      },
      { 
        id: "id_verification", 
        text: "What systems do you have for verifying third-party identities when they interact with your organization?", 
        options: [
          "Minimal verification of third-party identities", 
          "Basic email domain and contact verification", 
          "Multi-factor verification of third-party identities with callback procedures", 
          "Advanced third-party identity management platform with continuous validation"
        ],
        explanation: "Third-party identity verification helps prevent business email compromise and vendor impersonation attacks."
      }
    ],
    // Data Access Monitoring
    [
      { 
        id: "data_classification", 
        text: "How do you classify and label sensitive data to enable targeted monitoring and protection?", 
        options: [
          "No formal data classification system", 
          "Basic manual classification of obviously sensitive data", 
          "Automated classification based on content scanning", 
          "Dynamic classification with context awareness and machine learning"
        ],
        explanation: "Data classification is fundamental to applying appropriate controls and monitoring for different sensitivity levels."
      },
      { 
        id: "data_dlp", 
        text: "What data loss prevention (DLP) measures do you employ to detect and prevent data exfiltration?", 
        options: [
          "No DLP solutions implemented", 
          "Basic endpoint DLP for obviously sensitive data types (SSNs, CCNs)", 
          "Network and endpoint DLP with custom policies", 
          "Advanced DLP with behavioral analytics, OCR capabilities, and machine learning"
        ],
        explanation: "DLP is critical for detecting and preventing data theft, particularly by insiders with legitimate access."
      },
      { 
        id: "data_unusual", 
        text: "How do you detect unusual or suspicious data access patterns that may indicate insider threats?", 
        options: [
          "No systematic detection of unusual access patterns", 
          "Manual periodic reviews of access logs", 
          "Automated alerts for predefined suspicious patterns", 
          "AI-based anomaly detection with user behavior analytics and entity profiling"
        ],
        explanation: "Advanced analytics can identify access patterns that deviate from normal behavior and may indicate malicious activity."
      },
      { 
        id: "data_logs", 
        text: "How long do you retain access logs and audit trails to support investigations of potential insider activities?", 
        options: [
          "Less than 30 days of log retention", 
          "30-90 days of log retention with basic search", 
          "90-365 days of log retention with advanced search", 
          "Over 1 year of log retention with advanced search and analytics capabilities"
        ],
        explanation: "Comprehensive log retention enables investigation of suspicious activities that may only be discovered long after they occur."
      },
      { 
        id: "data_encryption", 
        text: "How do you use encryption to protect sensitive data from unauthorized access by insiders?", 
        options: [
          "Minimal use of encryption for data protection", 
          "Basic encryption for obviously sensitive data at rest", 
          "Comprehensive encryption for data at rest and in transit", 
          "End-to-end encryption with granular access controls and key management"
        ],
        explanation: "Encryption helps ensure that even if insiders access data, they cannot read it without proper authorization."
      }
    ],
    // Employee Behavior Analytics
    [
      { 
        id: "behavior_baseline", 
        text: "How do you establish behavioral baselines to detect deviations that may indicate insider threat activity?", 
        options: [
          "No behavioral baselines established", 
          "Basic baselines for login times and access patterns", 
          "Detailed baselines including peer group comparisons", 
          "Dynamic baselines using AI that adapt to changing job roles and contexts"
        ],
        explanation: "Behavioral baselines allow the detection of unusual activities that deviate from an employee's normal patterns."
      },
      { 
        id: "behavior_indicators", 
        text: "How do you monitor for behavioral indicators that may signal insider risk or compromise?", 
        options: [
          "No monitoring of behavioral indicators", 
          "Basic monitoring of obvious risk indicators", 
          "Advanced monitoring of multiple risk indicators", 
          "Predictive monitoring with contextual analysis and risk scoring"
        ],
        explanation: "Behavioral indicators can reveal potential insider threats before data is actually compromised."
      },
      { 
        id: "behavior_reporting", 
        text: "What mechanisms exist for employees to anonymously report concerning behaviors or suspected insider threats?", 
        options: [
          "No anonymous reporting mechanism", 
          "Basic email reporting to security team", 
          "Dedicated reporting tool with anonymity protections", 
          "Advanced reporting system with follow-up workflows and whistleblower protections"
        ],
        explanation: "Peer reporting is often how insider threats are first detected, making anonymous reporting channels critical."
      },
      { 
        id: "behavior_investigations", 
        text: "What process do you follow for investigating potential insider threats based on behavioral indicators?", 
        options: [
          "Ad-hoc investigations without formal process", 
          "Basic investigation process without specialized tools", 
          "Formal investigation process with legal oversight", 
          "Comprehensive cross-functional investigation process with specialized forensic tools"
        ],
        explanation: "A structured investigation process ensures thoroughness while protecting privacy and legal rights."
      },
      { 
        id: "behavior_quiet_quitting", 
        text: "How do you detect and address 'quiet quitting' behaviors that may increase insider risk?", 
        options: [
          "No specific measures to detect disengagement", 
          "Basic manager awareness of engagement issues", 
          "Formal monitoring of engagement and productivity metrics", 
          "Advanced analytics correlating disengagement with security behaviors and risk indicators"
        ],
        explanation: "Disengaged employees may be more likely to engage in risky behaviors or be susceptible to social engineering."
      }
    ],
    // Offboarding Procedures
    [
      { 
        id: "offboard_process", 
        text: "How robust is your offboarding process in mitigating security risks when employees leave?", 
        options: [
          "Manual checklist without comprehensive coverage", 
          "Automated basic process covering account deactivation", 
          "Comprehensive process with multiple checkpoints and validations", 
          "Advanced process with verification, forensic checks, and continuous monitoring"
        ],
        explanation: "A thorough offboarding process prevents former employees from retaining access to systems and data."
      },
      { 
        id: "offboard_access", 
        text: "How quickly are access rights and credentials removed after an employee's termination?", 
        options: [
          "Access removal may take over a week", 
          "Access removal within one week", 
          "Access removal within 24 hours", 
          "Immediate access removal through automated workflows"
        ],
        explanation: "Rapid access removal is critical to prevent unauthorized access by former employees."
      },
      { 
        id: "offboard_monitoring", 
        text: "Do you implement enhanced monitoring before planned termination to detect potential data theft?", 
        options: [
          "No enhanced monitoring before termination", 
          "Basic monitoring of obvious risk indicators", 
          "Advanced monitoring of system access and data transfers", 
          "Predictive monitoring that identifies resignation risk and automatically enhances surveillance"
        ],
        explanation: "The period before termination represents a high-risk window for data theft and sabotage."
      },
      { 
        id: "offboard_data", 
        text: "What measures do you take to prevent data exfiltration during the offboarding process?", 
        options: [
          "No specific measures beyond normal operations", 
          "Basic DLP alerts for large file transfers", 
          "Advanced DLP with monitoring of personal emails and cloud storage", 
          "Comprehensive prevention with forensic verification and legal hold capabilities"
        ],
        explanation: "Specialized data protection during offboarding can catch attempted data theft before an employee departs."
      },
      {
        id: "offboard_agreements",
        text: "How do you manage non-disclosure and non-compete agreements during offboarding?",
        options: [
          "Basic reminder of existing agreements",
          "Formal exit interview reviewing obligations",
          "Comprehensive legal review with signed reaffirmation",
          "Advanced tracking of post-employment compliance with legal support"
        ],
        explanation: "Clear reinforcement of legal obligations can deter former employees from misusing company information."
      }
    ],
    // AI & GenAI Controls
    [
      {
        id: "ai_genai_policy",
        text: "Do you have enforceable policies and technical controls preventing employees from inputting sensitive data into external AI tools (ChatGPT, Gemini, Copilot, etc.)?",
        options: [
          "No policy or monitoring exists for AI tool usage",
          "Awareness-only policy with no technical enforcement",
          "Documented policy with DLP rules blocking known AI endpoints",
          "Comprehensive AI governance with approved enterprise AI tools, DLP enforcement, and continuous shadow AI discovery"
        ],
        explanation: "Employees routinely paste confidential data — customer PII, source code, financial records — into external LLMs. Without controls, this represents an unmonitored exfiltration channel invisible to traditional security tools."
      },
      {
        id: "ai_shadow_detection",
        text: "How do you detect and govern unauthorized (shadow) AI tool usage across your organization?",
        options: [
          "No visibility into AI tool usage by employees",
          "Basic web filtering blocking only known AI sites",
          "Active monitoring of AI API traffic with exception reporting and alerts",
          "Comprehensive shadow AI governance with CASB integration, approved alternatives, and continuous discovery"
        ],
        explanation: "Shadow AI follows the same risk pattern as shadow IT — employees route around security controls using preferred tools, creating blind spots for data leakage and compliance violations."
      },
      {
        id: "ai_social_engineering",
        text: "What defenses protect employees against AI-generated phishing, voice cloning (vishing), and hyper-personalized social engineering attacks?",
        options: [
          "No awareness of AI-enhanced social engineering threats",
          "Basic phishing training without AI-specific threat content",
          "Updated training with AI social engineering simulations and voice cloning awareness",
          "Advanced defenses including real-time AI-generated content detection, continuous adaptive training, and verified out-of-band communication channels"
        ],
        explanation: "Threat actors now use LLMs to craft flawless spear-phishing at scale and clone executive voices for vishing attacks. Traditional phishing awareness training is no longer sufficient against these threats."
      },
      {
        id: "ai_agentic_risk",
        text: "How do you govern autonomous AI agents that have persistent access to your systems, APIs, databases, and sensitive data?",
        options: [
          "No awareness or governance for AI agents or autonomous systems",
          "Basic awareness but no formal controls for AI agent deployment",
          "Formal approval process with access reviews and scoped permissions for AI agents",
          "Comprehensive agentic AI governance with continuous monitoring, least-privilege access, full audit trails, and kill-switch capabilities"
        ],
        explanation: "Agentic AI systems operate autonomously with broad access and can be compromised via prompt injection or misconfiguration, creating a new class of insider threat vector that bypasses user-centric monitoring."
      },
      {
        id: "ai_fraudulent_workers",
        text: "What controls exist to detect fraudulent remote hires and nation-state-placed insiders (e.g., North Korean IT worker schemes flagged by CISA and the FBI)?",
        options: [
          "No specific controls for detecting fraudulent remote hires beyond standard hiring",
          "Basic identity verification during hiring with no ongoing assurance",
          "Enhanced onboarding verification with liveness detection and re-verification triggers",
          "Advanced controls including geolocation monitoring, behavioral analytics cross-referenced with known fraud indicators, and government watchlist screening"
        ],
        explanation: "CISA, FBI, and international agencies have issued repeated 2024–2026 alerts about DPRK and other state actors placing fraudulent IT workers inside organizations to fund state programs, steal IP, and install backdoors."
      }
    ]
  ];
  
  const handleAnswer = async (questionId: string, value: number): Promise<void> => {
    if (!(await ensureStarted())) return; // gate overlay shown — ignore until entitled
    const next = {...answers, [questionId]: value};
    setAnswers(next);
    try { localStorage.setItem('insiderrisk_answers', JSON.stringify(next)); } catch { /* ignore */ }
  };
  
  const calculateScores = (): Scores => {
    const categoryScores: Record<string, CategoryScore> = {};
    const maxPossibleScore = 3; // Maximum score per question
    
    categories.forEach((category, index) => {
      const categoryQuestions = questions[index];
      let totalScore = 0;
      
      categoryQuestions.forEach(question => {
        const answer = answers[question.id];
        if (answer !== undefined) {
          totalScore += answer;
        }
      });
      
      // Calculate percentage score for the category
      const maxScore = categoryQuestions.length * maxPossibleScore;
      const percentageScore = (totalScore / maxScore) * 100;
      
      categoryScores[category] = {
        raw: totalScore,
        max: maxScore,
        percentage: percentageScore
      };
    });
    
    // Calculate overall score
    let totalScore = 0;
    let totalMaxScore = 0;
    
    Object.values(categoryScores).forEach(score => {
      totalScore += score.raw;
      totalMaxScore += score.max;
    });
    
    const overallPercentage = (totalScore / totalMaxScore) * 100;
    
    return {
      categories: categoryScores,
      overall: {
        raw: totalScore,
        max: totalMaxScore,
        percentage: overallPercentage
      }
    };
  };
  
  const getMaturityLevel = (percentage: number): string => {
    if (percentage < 25) return "Initial";
    if (percentage < 50) return "Developing";
    if (percentage < 75) return "Established";
    return "Optimized";
  };
  
  const getRecommendations = (scores: Scores): Recommendation[] => {
    const recommendations: Recommendation[] = [];
    
    // Add overall recommendations
    const overallMaturity = getMaturityLevel(scores.overall.percentage);
    
    recommendations.push({
      category: "Overall Program",
      maturity: overallMaturity,
      recommendations: [
        overallMaturity === "Initial" ? "Establish a formal insider threat program with executive sponsorship" :
        overallMaturity === "Developing" ? "Formalize insider threat procedures and increase cross-functional collaboration" :
        overallMaturity === "Established" ? "Implement advanced analytics and enhance detection capabilities" :
        "Focus on optimization and integration of insider threat controls with broader security operations"
      ]
    });
    
    // Add category-specific recommendations
    Object.entries(scores.categories).forEach(([category, score]) => {
      const maturity = getMaturityLevel(score.percentage);
      
      let categoryRecs: string[] = [];
      
      switch(category) {
        case "Remote Work Controls":
          if (maturity === "Initial") categoryRecs = ["Establish basic remote work security policies", "Implement MFA for all remote access"];
          else if (maturity === "Developing") categoryRecs = ["Deploy endpoint management solutions", "Enhance VPN security controls"];
          else if (maturity === "Established") categoryRecs = ["Implement zero-trust architecture", "Deploy advanced user behavior analytics"];
          else categoryRecs = ["Optimize remote work security through continuous assessment", "Integrate remote monitoring with threat intelligence"];
          break;
          
        case "Identity Verification":
          if (maturity === "Initial") categoryRecs = ["Enhance onboarding identity verification", "Implement basic background checks"];
          else if (maturity === "Developing") categoryRecs = ["Deploy IAM solutions with workflow automation", "Implement deepfake detection in hiring processes"];
          else if (maturity === "Established") categoryRecs = ["Implement continuous identity verification", "Enhance biometric validation"];
          else categoryRecs = ["Optimize identity verification processes with advanced AI", "Integrate identity verification with threat intelligence"];
          break;
          
        case "Data Access Monitoring":
          if (maturity === "Initial") categoryRecs = ["Implement data classification", "Deploy basic DLP controls"];
          else if (maturity === "Developing") categoryRecs = ["Enhance DLP with better detection capabilities", "Improve audit logging retention"];
          else if (maturity === "Established") categoryRecs = ["Implement user behavior analytics for data access", "Enhance anomaly detection"];
          else categoryRecs = ["Optimize data monitoring through machine learning", "Integrate data access monitoring with threat hunting"];
          break;
          
        case "Employee Behavior Analytics":
          if (maturity === "Initial") categoryRecs = ["Establish behavior baselines", "Implement basic monitoring"];
          else if (maturity === "Developing") categoryRecs = ["Enhance behavior monitoring capabilities", "Implement anonymous reporting tools"];
          else if (maturity === "Established") categoryRecs = ["Deploy advanced analytics for behavior monitoring", "Enhance investigation processes"];
          else categoryRecs = ["Optimize behavior analytics with predictive capabilities", "Integrate behavior analytics with insider risk scoring"];
          break;
          
        case "Offboarding Procedures":
          if (maturity === "Initial") categoryRecs = ["Formalize offboarding procedures", "Implement access revocation processes"];
          else if (maturity === "Developing") categoryRecs = ["Enhance monitoring during offboarding", "Implement DLP controls for departing employees"];
          else if (maturity === "Established") categoryRecs = ["Deploy advanced monitoring for pre-termination risk", "Enhance data exfiltration controls"];
          else categoryRecs = ["Optimize offboarding through automated workflows", "Integrate offboarding with insider threat monitoring"];
          break;

        case "AI & GenAI Controls":
          if (maturity === "Initial") categoryRecs = ["Establish an AI acceptable use policy with data classification guidance", "Deploy web filtering to block access to unauthorized external AI tools"];
          else if (maturity === "Developing") categoryRecs = ["Implement DLP rules targeting known AI API endpoints and file uploads", "Add AI-specific voice cloning and social engineering content to security training"];
          else if (maturity === "Established") categoryRecs = ["Integrate CASB for comprehensive shadow AI discovery and governance", "Establish an agentic AI risk framework with least-privilege access controls"];
          else categoryRecs = ["Optimize AI governance with continuous shadow AI discovery and approved enterprise alternatives", "Integrate fraudulent worker detection into your identity assurance and behavioral analytics program"];
          break;
      }
      
      recommendations.push({
        category,
        maturity,
        recommendations: categoryRecs
      });
    });
    
    return recommendations;
  };
  
  const nextStep = (): void => {
    if (currentStep < categories.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowResults(true);
      // Save to the member portal's Reports tab (logged-in runs only; no-op for guests).
      const gate = (window as unknown as { csmGate?: { isLoggedIn?: () => boolean; saveReport?: (r: unknown) => Promise<boolean> } }).csmGate;
      if (gate?.isLoggedIn?.() && gate.saveReport) {
        const scores = calculateScores();
        gate.saveReport({ title: 'Insider Risk Assessment', summary: `Insider risk posture — ${Math.round(scores.overall.percentage)}%`, data: { answers, scores } });
      }
    }
  };
  
  const prevStep = (): void => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const restart = (): void => {
    setAnswers({});
    setCurrentStep(0);
    setShowResults(false);
  };
  
  // Progress bar calculation
  const progress = ((currentStep + 1) / categories.length) * 100;
  
  const renderQuestion = (question: Question): JSX.Element => {
    return (
      <div key={question.id} className="mb-8 bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
        <div className="mb-4">
          <h3 className="font-medium text-gray-800 text-lg mb-2">{question.text}</h3>
          {question.explanation && (
            <div className="bg-blue-50 p-3 rounded text-sm text-gray-700 border-l-2 border-blue-300">
              <strong className="text-blue-700">Why this matters:</strong> {question.explanation}
            </div>
          )}
        </div>
        <div className="space-y-3">
          {question.options.map((option: string, index: number) => (
            <div 
              key={index}
              onClick={() => handleAnswer(question.id, index)}
              className={`p-4 rounded-md cursor-pointer hover:bg-blue-50 transition-colors flex items-center ${answers[question.id] === index ? 'bg-blue-100 border border-blue-300' : 'bg-gray-50 border border-gray-200'}`}
            >
              <div className={`h-5 w-5 rounded-full mr-3 flex items-center justify-center ${answers[question.id] === index ? 'bg-blue-600' : 'bg-gray-200'}`}>
                {answers[question.id] === index && (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <div className="font-medium">{option}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {index === 0 ? 'High Risk' : 
                   index === 1 ? 'Moderate Risk' : 
                   index === 2 ? 'Low Risk' : 'Minimal Risk'}
                </div>
              </div>
              <div className="ml-2">
                <div className={`h-6 w-6 rounded-full ${
                  index === 0 ? 'bg-red-500' : 
                  index === 1 ? 'bg-yellow-500' : 
                  index === 2 ? 'bg-blue-500' : 'bg-green-500'
                }`}></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  const renderCurrentStep = (): JSX.Element => {
    return (
      <div>
        <div className="mb-6">
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${progress}%` }}></div>
          </div>
        </div>
      
        <h2 className="text-xl font-bold mb-4 text-gray-700">{categories[currentStep]}</h2>
        {questions[currentStep].map(question => renderQuestion(question))}
        
        <div className="flex justify-between mt-6">
          <button 
            onClick={prevStep}
            disabled={currentStep === 0}
            className={`px-4 py-2 rounded ${currentStep === 0 ? 'bg-gray-300 cursor-not-allowed' : 'bg-gray-500 hover:bg-gray-600 text-white'}`}
          >
            Previous
          </button>
          <button 
            onClick={nextStep}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            {currentStep === categories.length - 1 ? 'View Results' : 'Next'}
          </button>
        </div>
      </div>
    );
  };
  
  const renderResults = (): JSX.Element => {
    const scores = calculateScores();
    const recommendations = getRecommendations(scores);
    
    // Industry-specific risk context
    const industryContext: Record<string, string> = {
      financial: "Financial institutions face heightened insider risks due to access to sensitive financial data and funds. State-sponsored actors specifically target financial employees for recruitment, and GenAI tools create new pathways for unmonitored data exfiltration.",
      healthcare: "Healthcare organizations must protect patient data (PHI) from insider threats while maintaining efficient access for patient care. The rise of agentic AI in clinical workflows introduces new governance requirements for autonomous system access.",
      government: "Government agencies are prime targets for foreign intelligence services — particularly DPRK and China — seeking to place insiders or recruit existing employees. AI-generated deepfakes have made identity fraud during hiring significantly harder to detect.",
      technology: "Technology companies face acute risks from IP and source code exfiltration, especially as employees paste proprietary code into external AI coding tools. Nation-state fraudulent IT worker schemes disproportionately target tech companies.",
      manufacturing: "Manufacturing organizations must protect proprietary processes and designs from insider theft, particularly as AI-assisted design tools and global supply chains expand the potential exfiltration surface.",
      energy: "Critical infrastructure operators face nation-state actors placing insiders to enable long-term access for potential sabotage. Operational technology environments require specialized behavioral monitoring distinct from IT-focused programs.",
      general: "All organizations face rapidly evolving insider threats in 2026: GenAI data leakage, AI-assisted social engineering at scale, agentic AI governance gaps, and nation-state fraudulent remote worker schemes now demand dedicated controls beyond traditional programs."
    };
    
    // Size-specific risk context
    const sizeContext: Record<string, string> = {
      small: "Small organizations often lack dedicated security resources but can implement targeted controls to address their highest insider risks.",
      medium: "Mid-sized organizations need to balance comprehensive controls with limited security resources, focusing on automation and efficiency.",
      large: "Large organizations require formalized insider threat programs with dedicated personnel and cross-functional governance.",
      enterprise: "Enterprise organizations face complex insider risks across business units and geographies, requiring sophisticated detection and analytics."
    };
    
    const generatePDF = async () => {
      if (!(await ensureStarted())) return; // gate the deliverable too
      const element = document.createElement('div');
      element.innerHTML = `
        <div style="max-width: 800px; margin: 0 auto; padding: 20px; font-family: Arial, sans-serif;">
          <div style="text-align: center; margin-bottom: 30px;">
            <h1 style="color: #3b82f6; margin-bottom: 10px;">Insider Threat Risk Assessment Results</h1>
            <p>Completed on ${new Date().toLocaleDateString()}</p>
          </div>

          <div style="margin-bottom: 30px;">
            <h2 style="color: #1f2937; margin-bottom: 15px;">Overall Maturity: ${getMaturityLevel(scores.overall.percentage)}</h2>
            <p style="margin-bottom: 15px;">Overall Score: ${Math.round(scores.overall.percentage)}%</p>
            <p style="margin-bottom: 20px;">${industryContext[industryType] || industryContext['general']}</p>
          </div>

          <div style="margin-bottom: 30px;">
            <h2 style="color: #1f2937; margin-bottom: 15px;">Category Scores</h2>
            ${Object.entries(scores.categories)
              .map(([category, score]) => `
                <div style="margin-bottom: 15px;">
                  <h3 style="margin-bottom: 5px;">${category}: ${Math.round(score.percentage)}%</h3>
                  <p>Maturity Level: ${getMaturityLevel(score.percentage)}</p>
                </div>
              `).join('')}
          </div>

          <div style="margin-bottom: 30px;">
            <h2 style="color: #1f2937; margin-bottom: 15px;">Recommendations</h2>
            ${recommendations
              .map(rec => `
                <div style="margin-bottom: 20px;">
                  <h3 style="color: #4b5563; margin-bottom: 10px;">${rec.category} (${rec.maturity})</h3>
                  <ul style="padding-left: 20px;">
                    ${rec.recommendations.map(item => `<li style="margin-bottom: 5px;">${item}</li>`).join('')}
                  </ul>
                </div>
              `).join('')}
          </div>

          <div style="margin-bottom: 30px;">
            <h2 style="color: #1f2937; margin-bottom: 15px;">Site Sponsors</h2>
            <div style="display: flex; flex-wrap: wrap; justify-content: center; gap: 15px;">
              <div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px; text-align: center; width: 180px;">
                <h3 style="color: #3b82f6; margin-bottom: 5px;">Security Careers Help</h3>
                <p style="font-size: 12px; color: #6b7280;">securitycareers.help</p>
              </div>
              <div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px; text-align: center; width: 180px;">
                <h3 style="color: #3b82f6; margin-bottom: 5px;">CISO Marketplace</h3>
                <p style="font-size: 12px; color: #6b7280;">cisomarketplace.services</p>
              </div>
              <div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px; text-align: center; width: 180px;">
                <h3 style="color: #3b82f6; margin-bottom: 5px;">Cyber Insurance Calc</h3>
                <p style="font-size: 12px; color: #6b7280;">cyberinsurancecalc.com</p>
              </div>
              <div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px; text-align: center; width: 180px;">
                <h3 style="color: #3b82f6; margin-bottom: 5px;">CISO Budget Builder</h3>
                <p style="font-size: 12px; color: #6b7280;">cisobudgetbuilder.com</p>
              </div>
              <div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px; text-align: center; width: 180px;">
                <h3 style="color: #3b82f6; margin-bottom: 5px;">IR Maturity Assessment</h3>
                <p style="font-size: 12px; color: #6b7280;">irmaturityassessment.com</p>
              </div>
            </div>
          </div>

          <div style="text-align: center; font-size: 12px; color: #6b7280; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
            <p>This report was generated by Insider Threat Risk Profiler. Find more security resources at securitycareers.help</p>
          </div>
        </div>
      `;

      html2pdf()
        .from(element)
        .save('insider_threat_risk_assessment.pdf');
    };

    return (
      <div>
        <div className="bg-blue-600 p-6 rounded-lg shadow-lg mb-8 text-white">
          <h2 className="text-2xl font-bold mb-2 text-center">Insider Threat Risk Assessment Results</h2>
          <p className="text-center opacity-90">Completed on {new Date().toLocaleDateString()}</p>
        </div>
        
        <div className="mb-8 p-6 bg-white rounded-lg shadow-lg border border-gray-200">
          <div className="flex flex-col md:flex-row items-center mb-6">
            <div className="w-full md:w-1/3 flex justify-center">
              <div className="relative w-48 h-48">
                <svg viewBox="0 0 36 36" className="w-48 h-48 transform -rotate-90">
                  <path
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="3"
                  />
                  <path
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke={scores.overall.percentage < 25 ? '#ef4444' : 
                           scores.overall.percentage < 50 ? '#eab308' : 
                           scores.overall.percentage < 75 ? '#3b82f6' : '#22c55e'}
                    strokeWidth="3"
                    strokeDasharray={`${scores.overall.percentage}, 100`}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-4xl font-bold">{Math.round(scores.overall.percentage)}%</span>
                  <span className="text-sm text-gray-500">Overall Score</span>
                </div>
              </div>
            </div>
            
            <div className="w-full md:w-2/3 mt-6 md:mt-0">
              <div className="mb-3">
                <span className="font-medium text-lg">Maturity Level: </span>
                <span className={`py-1 px-3 rounded-full text-lg font-medium ${
                  scores.overall.percentage < 25 ? 'bg-red-100 text-red-800' :
                  scores.overall.percentage < 50 ? 'bg-yellow-100 text-yellow-800' :
                  scores.overall.percentage < 75 ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                }`}>
                  {getMaturityLevel(scores.overall.percentage)}
                </span>
              </div>
              
              <div className="mb-4 p-4 rounded-lg bg-gray-50 border border-gray-200">
                <h4 className="font-medium mb-2">Key Risk Insights:</h4>
                <p className="text-gray-700">
                  {industryContext[industryType] || industryContext['general']}
                </p>
                <p className="text-gray-700 mt-2">
                  {sizeContext[companySize] || sizeContext['medium']}
                </p>
              </div>
              
              <div className="p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                <p className="text-sm text-yellow-800">
                  <strong>Highest Priority Focus Areas:</strong> {
                    Object.entries(scores.categories)
                      .sort((a, b) => a[1].percentage - b[1].percentage)
                      .slice(0, 2)
                      .map(([category]) => category)
                      .join(' and ')
                  }
                </p>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
            {Object.entries(scores.categories).map(([category, score]) => {
              const maturityLevel = getMaturityLevel(score.percentage);
              const maturityColors = {
                "Initial": "bg-red-500",
                "Developing": "bg-yellow-500",
                "Established": "bg-blue-500",
                "Optimized": "bg-green-500"
              };
              
              return (
                <div key={category} className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                  <div className={`h-2 ${maturityColors[maturityLevel as keyof typeof maturityColors]}`}></div>
                  <div className="p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium">{category}</h4>
                      <span className={`py-1 px-2 rounded-full text-xs ${
                        score.percentage < 25 ? 'bg-red-100 text-red-800' :
                        score.percentage < 50 ? 'bg-yellow-100 text-yellow-800' :
                        score.percentage < 75 ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {maturityLevel}
                      </span>
                    </div>
                    <div className="mb-2">
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className={`h-3 rounded-full ${
                            score.percentage < 25 ? 'bg-red-500' :
                            score.percentage < 50 ? 'bg-yellow-500' :
                            score.percentage < 75 ? 'bg-blue-500' : 'bg-green-500'
                          }`} 
                          style={{ width: `${score.percentage}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-xs mt-1">
                        <span>High Risk</span>
                        <span>{Math.round(score.percentage)}%</span>
                        <span>Low Risk</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        <div className="mb-8 bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-blue-600 p-4">
            <h3 className="text-xl font-semibold text-white">Maturity Model Explained</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="bg-red-100 text-red-800 py-1 px-2 rounded-full text-sm font-medium">Initial</span>
                  <span className="ml-2 text-red-500">&lt;25%</span>
                </div>
                <p className="text-sm text-gray-700">Organizations at this level have minimal insider threat controls, with ad-hoc processes and limited detection capabilities. Remote workers pose significant risk due to lack of oversight.</p>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="bg-yellow-100 text-yellow-800 py-1 px-2 rounded-full text-sm font-medium">Developing</span>
                  <span className="ml-2 text-yellow-500">25-50%</span>
                </div>
                <p className="text-sm text-gray-700">Organizations have established basic policies and some monitoring capabilities, but gaps remain in detection of sophisticated threats like deepfakes and advanced social engineering.</p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="bg-blue-100 text-blue-800 py-1 px-2 rounded-full text-sm font-medium">Established</span>
                  <span className="ml-2 text-blue-500">50-75%</span>
                </div>
                <p className="text-sm text-gray-700">Organizations have comprehensive controls with automation and proactive monitoring. Remote work security is well-managed, and behavioral analytics are deployed to detect anomalies.</p>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="bg-green-100 text-green-800 py-1 px-2 rounded-full text-sm font-medium">Optimized</span>
                  <span className="ml-2 text-green-500">&gt;75%</span>
                </div>
                <p className="text-sm text-gray-700">Organizations have advanced, integrated controls with predictive capabilities. Continuous monitoring, AI-driven analytics, and adaptive security measure effectively address evolving threats.</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mb-8 bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-blue-600 p-4">
            <h3 className="text-xl font-semibold text-white">Strategic Recommendations</h3>
          </div>
          
          <div className="p-6">
            <div className="mb-6">
              <h4 className="font-semibold text-lg text-gray-800 mb-3">High-Priority Actions</h4>
              <div className="space-y-4">
                {recommendations
                  .filter(rec => rec.maturity === "Initial" || rec.maturity === "Developing")
                  .map((rec, index) => (
                    <div key={index} className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex justify-between items-center mb-3">
                        <h5 className="font-medium text-yellow-800">{rec.category}</h5>
                        <span className={`py-1 px-2 rounded-full text-xs ${
                          rec.maturity === "Initial" ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {rec.maturity}
                        </span>
                      </div>
                      <ul className="space-y-2">
                        {rec.recommendations.map((item, i) => (
                          <li key={i} className="flex items-start">
                            <svg className="h-5 w-5 text-yellow-500 mr-2 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg text-gray-800 mb-3">Continuous Improvement</h4>
              <div className="space-y-4">
                {recommendations
                  .filter(rec => rec.maturity === "Established" || rec.maturity === "Optimized")
                  .map((rec, index) => (
                    <div key={index} className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex justify-between items-center mb-3">
                        <h5 className="font-medium text-blue-800">{rec.category}</h5>
                        <span className={`py-1 px-2 rounded-full text-xs ${
                          rec.maturity === "Established" ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {rec.maturity}
                        </span>
                      </div>
                      <ul className="space-y-2">
                        {rec.recommendations.map((item, i) => (
                          <li key={i} className="flex items-start">
                            <svg className="h-5 w-5 text-blue-500 mr-2 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h3 className="text-xl font-semibold mb-2">2026 Emerging Threat Landscape</h3>
          <p className="text-sm text-gray-500 mb-5">Critical insider threat vectors that have escalated significantly heading into 2026</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-red-200 rounded-lg p-4 bg-red-50">
              <div className="flex items-center mb-2">
                <span className="bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded mr-2">NEW 2026</span>
                <h4 className="font-medium text-red-900">GenAI Data Exfiltration</h4>
              </div>
              <p className="text-sm text-gray-700 mb-3">
                Employees routinely paste sensitive data — source code, PII, financial models, M&A details — into external LLMs like ChatGPT and Gemini, creating unmonitored exfiltration channels invisible to traditional DLP tools.
              </p>
              <div className="text-sm bg-white p-3 rounded border border-red-200">
                <strong>Defense Strategy:</strong> Deploy AI-aware DLP with API traffic inspection, establish approved enterprise AI alternatives, and train employees on AI data classification hygiene.
              </div>
            </div>

            <div className="border border-red-200 rounded-lg p-4 bg-red-50">
              <div className="flex items-center mb-2">
                <span className="bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded mr-2">NEW 2026</span>
                <h4 className="font-medium text-red-900">Nation-State IT Worker Infiltration</h4>
              </div>
              <p className="text-sm text-gray-700 mb-3">
                DPRK and other state actors place fraudulent IT workers inside organizations using stolen identities, deepfakes, and AI-generated resumes — earning salaries while exfiltrating IP and installing long-term backdoors.
              </p>
              <div className="text-sm bg-white p-3 rounded border border-red-200">
                <strong>Defense Strategy:</strong> Implement liveness detection in video hiring, monitor work-from-location patterns, screen against government watchlists, and establish continuous identity assurance post-hire.
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-red-800 mb-2">Deepfake Impersonation</h4>
              <p className="text-sm text-gray-700 mb-3">
                AI-generated deepfakes and real-time voice cloning now bypass basic verification systems, enabling sophisticated impersonation of executives and remote workers in live video calls.
              </p>
              <div className="text-sm bg-gray-50 p-3 rounded border border-gray-200">
                <strong>Defense Strategy:</strong> Implement multi-factor verification and liveness detection during video calls; establish out-of-band verification procedures for sensitive requests.
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-red-800 mb-2">Agentic AI Insider Risk</h4>
              <p className="text-sm text-gray-700 mb-3">
                Autonomous AI agents with broad system access can be weaponized via prompt injection, misconfiguration, or supply chain compromise — bypassing user-centric monitoring entirely.
              </p>
              <div className="text-sm bg-gray-50 p-3 rounded border border-gray-200">
                <strong>Defense Strategy:</strong> Establish an agentic AI registry, enforce least-privilege access for all AI agents, implement continuous audit trails, and build kill-switch capabilities into deployment pipelines.
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-red-800 mb-2">Quiet Quitting & Multi-Job Security Risks</h4>
              <p className="text-sm text-gray-700 mb-3">
                Disengaged employees holding multiple remote jobs simultaneously may share credentials, grant unauthorized third-party access, or use AI tools to fake productivity — all while accumulating sensitive data.
              </p>
              <div className="text-sm bg-gray-50 p-3 rounded border border-gray-200">
                <strong>Defense Strategy:</strong> Deploy user behavior analytics correlating work patterns with data access; implement periodic re-verification and continuous engagement monitoring.
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-red-800 mb-2">AI-Enhanced Social Engineering</h4>
              <p className="text-sm text-gray-700 mb-3">
                Threat actors now use LLMs to generate flawless, hyper-personalized spear-phishing at scale and clone executive voices for vishing, making traditional detection training insufficient.
              </p>
              <div className="text-sm bg-gray-50 p-3 rounded border border-gray-200">
                <strong>Defense Strategy:</strong> Move beyond static phishing training to continuous adaptive simulations including AI-generated content; implement verified communication channels for sensitive requests.
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-between mt-8">
          <button 
            onClick={restart}
            className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium"
          >
            Restart Assessment
          </button>
          
          <button 
            onClick={generatePDF}
            disabled={!pdfReady}
            className={`px-6 py-3 rounded-lg font-medium ${
              pdfReady 
                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                : 'bg-blue-300 text-white cursor-not-allowed'
            }`}
          >
            Generate PDF Report
          </button>
        </div>
      </div>
    );
  };
  
  const renderIntro = (): JSX.Element => {
    return (
      <div className="bg-white p-6 rounded-lg shadow-lg mb-8">
        <h2 className="text-2xl font-bold text-blue-800 mb-4">Industry-Leading Insider Threat Assessment Tool</h2>
        
        <div className="prose max-w-none">
          <p className="text-gray-700 mb-4">In 2026, the insider threat landscape has fundamentally shifted. <strong>GenAI data exfiltration</strong>, <strong>nation-state fraudulent remote workers</strong>, <strong>agentic AI governance gaps</strong>, and <strong>AI-assisted social engineering</strong> have joined traditional risks like deepfakes and credential theft. Our updated assessment helps security leaders evaluate readiness across all six critical control domains.</p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-amber-900"><strong>2026 Alert:</strong> CISA and the FBI have issued active advisories on North Korean IT worker infiltration schemes and GenAI-enabled insider threats. This assessment now includes a dedicated <strong>AI & GenAI Controls</strong> category to evaluate your defenses against these emerging vectors.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
              <h3 className="font-semibold text-blue-700">Why Assess Your Insider Threat Risk in 2026?</h3>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>GenAI tools create invisible data exfiltration channels</li>
                <li>Nation-state actors now embed fraudulent remote workers</li>
                <li>Agentic AI systems bypass user-centric monitoring</li>
                <li>AI voice cloning defeats traditional identity verification</li>
                <li>Traditional controls are insufficient for today's threat actors</li>
                <li>Remote work continues to expand the insider attack surface</li>
              </ul>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
              <h3 className="font-semibold text-green-700">What This Assessment Delivers</h3>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Evaluate readiness across 6 critical insider risk domains</li>
                <li>Get 2026-current recommendations by maturity level</li>
                <li>Benchmark against NIST, CERT, and industry best practices</li>
                <li>Identify gaps before nation-state actors or GenAI exploits them</li>
                <li>Receive a tailored roadmap for your industry and organization size</li>
                <li>Export a full PDF report for leadership or board presentation</li>
              </ul>
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="font-semibold text-gray-700 mb-2">Customize Your Assessment</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Industry Type</label>
                <select 
                  value={industryType}
                  onChange={(e) => setIndustryType(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="general">General Business</option>
                  <option value="financial">Financial Services</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="government">Government / Defense</option>
                  <option value="technology">Technology</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="energy">Energy / Critical Infrastructure</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization Size</label>
                <select 
                  value={companySize}
                  onChange={(e) => setCompanySize(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="small">Small (1-100 employees)</option>
                  <option value="medium">Medium (101-1000 employees)</option>
                  <option value="large">Large (1001-5000 employees)</option>
                  <option value="enterprise">Enterprise (5000+ employees)</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mb-6">
            <p className="text-sm text-yellow-800"><strong>Note:</strong> This assessment takes approximately 5-10 minutes to complete and provides immediate results. Your responses are not stored or shared.</p>
          </div>
          
          <button 
            onClick={() => setCurrentStep(0)}
            className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            Begin Your Insider Threat Assessment
          </button>
        </div>
      </div>
    );
  };

  const renderCurrentStepNav = (): JSX.Element => {
    return (
      <div className="flex mb-6 bg-white p-4 rounded-lg shadow-md">
        {categories.map((category, index) => (
          <div
            key={index}
            className={`flex-1 text-center py-2 ${index < currentStep ? 'text-blue-600' : index === currentStep ? 'bg-blue-50 border-b-2 border-blue-600 font-medium' : 'text-gray-500'}`}
          >
            <div className="hidden md:block">{category}</div>
            <div className="md:hidden">{index + 1}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-gray-50 min-h-screen">
      <header className="mb-8">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">
            <span className="text-blue-600">Insider Threat</span> Risk Profiler
          </h1>
          <p className="text-gray-600 mt-2 text-lg">Assess, Quantify, and Mitigate Your Organization's Insider Risk</p>
        </div>
        
        {currentStep >= 0 && !showResults && renderCurrentStepNav()}
      </header>
      
      <main>
        {currentStep === -1 ? renderIntro() : (!showResults ? renderCurrentStep() : renderResults())}
      </main>
      
      <footer className="mt-12 text-center">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Why Insider Threat Management Matters</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="text-center">
              <div className="text-red-500 font-bold text-4xl mb-2">34%</div>
              <p className="text-sm text-gray-600">of data breaches involve an insider actor (Verizon DBIR)</p>
            </div>

            <div className="text-center">
              <div className="text-red-500 font-bold text-4xl mb-2">$16.2M</div>
              <p className="text-sm text-gray-600">average annual cost of insider-related incidents (Ponemon)</p>
            </div>

            <div className="text-center">
              <div className="text-red-500 font-bold text-4xl mb-2">76%</div>
              <p className="text-sm text-gray-600">of organizations report increased insider risk from GenAI adoption</p>
            </div>

            <div className="text-center">
              <div className="text-red-500 font-bold text-4xl mb-2">308 days</div>
              <p className="text-sm text-gray-600">average time to detect and contain an insider incident</p>
            </div>
          </div>
          
          <div className="prose max-w-none text-left">
            <p className="text-gray-700">Our comprehensive Insider Threat Risk Profiler helps security professionals identify vulnerabilities in their organization's defenses against malicious insiders, negligent employees, and compromised credentials. By completing this assessment, you'll receive actionable insights to strengthen your security posture.</p>
            
            <p className="text-sm text-gray-500 mt-4">This tool is designed to provide general guidance and should not replace a comprehensive security audit by qualified professionals. Results are based on industry best practices and frameworks including NIST SP 800-53, ISO 27001, and the CERT Insider Threat Center's publications.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default InsiderThreatRiskProfiler;
